import React, { useContext, useEffect, useState, useRef } from "react";
import { MatchContext } from "../../../createContext/Match/MatchContext";
import IsAuth from "../../../createContext/is-Auth/IsAuthContext";
import TeamIcon1 from "../../images/TeamIcon1.png";
import TeamIcon2 from "../../images/TeamIcon2.png";
import MatchCard from "../../RightSidebar/MatchCard";

const SearchDropdown = ({
  type,
  placeholder,
  onSelect,
  icon,
  isCustom = false,
  isMultiple = false,
  oldValue,
  preInput
}) => {
  const [options, setOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isInvalidSport, setIsInvalidSport] = useState(false); // Track if selected sport is invalid
  const [isFocused, setIsFocused] = useState(false); // Track if input field is focused

  const { sports, players } = useContext(MatchContext);
  const { matches } = useContext(MatchContext);
  const { user } = useContext(IsAuth);

  const dropdownRef = useRef(null);

  // Close the dropdown when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Initialize selected options based on oldValue
  useEffect(() => {
    if (oldValue) {
      if (Array.isArray(oldValue)) {
        setSelectedOptions(oldValue);
        setSearchTerm(""); // Clear search when multiple items are selected
      } else {
        setSelectedOptions([oldValue]);
        setSearchTerm(oldValue.name || "");
      }
    }
  }, [oldValue]);

  useEffect(()=>{
    if(type == "sports" && selectedOptions.length > 0){
      setIsInvalidSport(false);
    }
  },[selectedOptions])

  useEffect(() => {
    if (type === "sports") {
      setOptions(sports);
      setFilteredOptions(sports);
    } else if (type === "players" || type === "player") {
      setOptions(players);
      setFilteredOptions(players);
    } else if (type === "matches") {
      const today = new Date();
      if (preInput){
        const relevantMatches = matches.filter(match =>
          match._id === preInput
        );
        setOptions(relevantMatches);
        setFilteredOptions(relevantMatches);
        setSelectedOptions(relevantMatches);
        setSearchTerm(relevantMatches[0].name);
        if (onSelect) onSelect(relevantMatches[0]);
      }
      else{
        const relevantMatches = matches.filter(match =>
          match.admins.includes(user._id) &&
          match.status === "scheduled" &&
          new Date(match.date) < today
        );
        setOptions(relevantMatches);
        setFilteredOptions(relevantMatches);
      }
    }
  }, [type, sports, players, matches, user]);

  const handleSearchChange = (e) => {
    setIsOpen(true);
    const value = e.target.value;
    setSearchTerm(value);

    const filtered = options.filter((option) =>
      option.name?.toLowerCase().includes(value.toLowerCase())
    );

    if (isCustom && filtered.length === 0) {
      setIsOpen(false);
    }
    setFilteredOptions(filtered);
    
    if (type === "sports") {
      setIsOpen(true);
    }

    // Only check for invalid sport when focus is lost
    if (!isFocused && type === "sports") {
      if (sports.some(sport => sport.name.toLowerCase() === value.toLowerCase())) {
        setIsInvalidSport(false); // Valid sport, hide error message
      } else {
        setIsInvalidSport(true); // Invalid sport
      }
    }
  };

  const handleOptionClick = (option) => {
    if (isMultiple) {
      if (!selectedOptions.some(selected => selected._id === option._id)) {
        const newSelectedOptions = [...selectedOptions, option];
        setSelectedOptions(newSelectedOptions);
        setSearchTerm(""); // Clear search term for next selection
        if (onSelect) onSelect(newSelectedOptions);
      }
    } else {
      setSelectedOptions([option]);
      setSearchTerm(option.name);
      if (onSelect) onSelect(option);
    }
    setIsOpen(false);
  };

  const handleRemoveSelected = (optionToRemove) => {
    const newSelectedOptions = selectedOptions.filter(
      (option) => option._id !== optionToRemove._id
    );
    setSelectedOptions(newSelectedOptions);
    if (onSelect) onSelect(isMultiple ? newSelectedOptions : null);
  };

  const handleBlur = () => {
    setIsFocused(false); // Set focus to false when input loses focus

    // Check if the sport entered is valid when focus is lost
    if (type === "sports" && !sports.some(sport => sport.name.toLowerCase() === searchTerm.toLowerCase())) {
      setIsInvalidSport(true); // Show invalid sport message
    } else {
      setIsInvalidSport(false); // Remove invalid sport message if valid sport is entered
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    
    // Specifically for sports, show dropdown and reset filtered options when focused
    if (type === "sports") {
      setFilteredOptions(sports);
      setIsOpen(true);
    }
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div className="flex flex-col gap-2">
        {/* Selected Options Display */}
        {isMultiple && selectedOptions.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedOptions.map((option) => (
              <span
                key={option._id}
                className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-md"
              >
                <span>{option.name}</span>
                <button
                  onClick={() => handleRemoveSelected(option)}
                  className="text-gray-500 hover:text-gray-700 ml-1"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        
        {/* Search Input */}
        <div className="flex gap-1">
          <span className="my-auto text-black" aria-hidden="true">
            {icon}
          </span>
          <input
            type="text"
            placeholder={selectedOptions.length === 0 ? placeholder : "Add more..."}
            value={searchTerm}
            onChange={handleSearchChange}
            className="flex-grow w-fit rounded-lg border border-[#333] py-2 px-2.5 text-base"
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>

        {/* Show invalid sport message when focus is lost */}
        {isInvalidSport && type === "sports" && !isFocused && (
          <p className="text-red-500 text-sm" style={{marginLeft:"26px"}}>Please enter a valid sport.</p>
        )}
      </div>

      {/* Dropdown Options */}
      {isOpen && (
        <div className="absolute w-full mt-1 max-h-48 overflow-y-auto bg-white border border-gray-300 rounded-lg shadow-md z-50">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <p
                key={option._id}
                className={`px-3 py-2 cursor-pointer hover:bg-gray-100 text-base ${selectedOptions.some(selected => selected._id === option._id) ? "bg-gray-50 text-gray-500" : ""}`}
                onClick={() => handleOptionClick(option)}
              >
                {option.name}
              </p>
            ))
          ) : (
            <p className="px-3 py-2 text-center text-gray-500">
              No results found
            </p>
          )}
        </div>
      )}

      {/* Match Card Display */}
      {type === 'matches' && selectedOptions.length > 0 && (
        <div className="ml-5 mt-3 items-center -mb-5">
          <MatchCard
            type={selectedOptions[0].isTeamMatch ? "Team" : "1 on 1"}
            location={selectedOptions[0].location}
            team1={{ 
              name: selectedOptions[0].isTeamMatch 
                ? selectedOptions[0].teams[0].team.name 
                : selectedOptions[0].players[0].userName, 
              logo: selectedOptions[0].isTeamMatch 
                ? TeamIcon1 
                : selectedOptions[0].players[0].avatar 
            }}
            team2={{ 
              name: selectedOptions[0].isTeamMatch 
                ? selectedOptions[0].teams[1].team.name 
                : selectedOptions[0].players[1].userName, 
              logo: selectedOptions[0].isTeamMatch 
                ? TeamIcon2 
                : selectedOptions[0].players[1].avatar 
            }}
            date={new Date(selectedOptions[0].date).toLocaleString()}
          />
        </div>
      )}
    </div>
  );
};

export default SearchDropdown;